<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { getPresetActions } from '@/components/home/chat-action';
import ImageLazy from '@/components/ImageLazy.vue';
import { i18n } from '@/i18n';
import { request } from '@/lib/api';
import { useWindowSize } from '@vueuse/core';

const props = defineProps({
  modelValue: String,
  scene: Object,
});
const emit = defineEmits(['select']);
const allActions = ref(getPresetActions(i18n.locale));
const displayedActions = ref([]);
const taggedTemplates = ref([]);
const { width: windowWidth } = useWindowSize();
const containerRef = ref(null);

// Masonry layout logic
const masonryColumns = computed(() => {
  const list = displayedActions.value;
  if (!list.length) return [];

  let containerW = windowWidth.value || 1200;
  if (containerRef.value) {
    containerW = containerRef.value.clientWidth;
  }

  // Min 200px card width + 16px gap
  let cols = Math.floor((containerW + 16) / 216);
  cols = Math.max(1, Math.min(cols, 10));

  const columns = Array.from({ length: cols }, () => []);

  list.forEach((item, index) => {
    // Use simple round-robin distribution for predictable order
    const colIndex = index % cols;
    columns[colIndex].push(item);
  });

  return columns;
});

const matchedActions = computed(() => {
  const all = allActions.value;
  const tags = taggedCards.value;
  const list = all.filter((action) => action.tag === props.scene.tag);
  if (list.length) return [...tags, ...list];
  if (tags.length) return [...tags];
  return all;
});

// Initialize with 6 random presets
const refreshPresets = () => {
  const allActions = matchedActions.value;
  const shuffled = [...allActions].sort(() => 0.5 - Math.random());
  displayedActions.value = shuffled.slice(0, 6);
};

watch(
  () => props.scene,
  (scene) => {
    load(scene);
  }
);

function load(scene) {
  loadTaggedTemplate(scene).then(() => {
    refreshPresets();
  });
}

onMounted(() => {
  load(props.scene);
});

const taggedCards = computed(() => {
  const list = taggedTemplates.value;
  return list.map((item) => {
    const width = item.meta?.size?.w;
    const height = item.meta?.size?.h;
    const ratio = height && width ? width / height : 3 / 4;
    return {
      ratio: ratio,
      width: width,
      height: height,
      prompt: item.meta.prompt,
      image: item.thumbnail_path,
    };
  });
});

async function loadTaggedTemplate(scene) {
  const list = await request(`/shared/template/tag/${scene.tag || 'Untagged'}`);
  taggedTemplates.value = list;
}

function usePreset(preset) {
  emit('select', preset.prompt);
}
</script>
<template>
  <div class="flex flex-col mt-6">
    <!-- Preset Cards -->
    <div ref="containerRef" class="flex gap-4 w-full items-start mb-4 sm:mb-6 px-2 sm:px-0">
      <div
        v-for="(col, colIndex) in masonryColumns"
        :key="colIndex"
        class="flex flex-col gap-4 flex-1 min-w-0"
      >
        <div
          v-for="(preset, index) in col"
          :key="index"
          @click.stop="usePreset(preset)"
          class="group cursor-pointer overflow-hidden rounded-3xl bg-white p-1 border border-black/5 hover:border-black/20 w-full"
        >
          <div class="relative overflow-hidden rounded-2xl shadow-sm sm:shadow-md hover:shadow-lg">
            <div
              class="absolute z-10 inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300"
            ></div>
            <ImageLazy
              v-if="preset.image"
              :src="preset.image"
              :alt="preset.label"
              class="w-full transition-transform duration-300"
              :image-class="`object-cover w-full h-full scroll-on-hover`"
              :style="{
                aspectRatio: preset.ratio ? preset.ratio : '3/4',
                maxHeight: '60vh',
              }"
            />
            <div v-else>
              {{ preset }}
            </div>

            <!-- bottom Action Button -->
            <div
              class="absolute inset-0 z-20 flex items-end justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
            >
              <button
                class="flex items-center py-2 px-4 bg-black/90 backdrop-blur-lg text-white rounded-full transition-all duration-200 hover:bg-white shadow-lg shadow-black/10 border border-white/60 mb-3"
              >
                <span class="font-normal text-sm">
                  {{ $t('create.usePrompt') }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.scroll-on-hover) {
  object-position: center top;
  transition: object-position 5s ease-in-out;
}

.group:hover :deep(.scroll-on-hover) {
  object-position: center bottom;
}
</style>
